import {
  BasicPlaybook,
  Classification,
  IntegrationsLandingPagesTrackingSettings,
  Page,
  PagesCname,
  PagesEmbedSettings,
  PagesSubdomain,
  PagesUnfurlSettings,
  Playbook,
  PlaybookCategory,
  PlaybookConversationRatingSettings,
  PlaybookMetricsPageResponse,
  PlaybookReportType,
  PlaybookSortDirection,
  PlaybookSortProperty,
  PlaybookStatus,
  PlaybookVersionResponse,
  Result,
} from '@driftt/api-types'
import Qs from 'qs'
import QueryString from 'qs'
import { isNil, reject } from 'ramda'

import { ConversationAPI, CustomerAPI, PagesAPI, ReuseApi } from 'api'
import { PlaybookCollaboratorAccess } from 'types/Playbook'
import { isEmptyOrNil } from 'utils/index'

interface ArrayableFetchPlaybookPageParams {
  category?: PlaybookCategory[] | PlaybookCategory
  sortProperty?: PlaybookSortProperty
  sortDirection?: PlaybookSortDirection
  nameQuery?: string
  page?: number
  pageSize?: number
  includeMetrics?: boolean
  onlyMine?: boolean
  workspaceId?: string | string[]
}

/* PLAYBOOKS TABLE LIST APIs */
export const getPlaybooks = (params: ArrayableFetchPlaybookPageParams) => {
  const query = Qs.stringify(params, { arrayFormat: 'repeat' })
  return ConversationAPI.get<PlaybookMetricsPageResponse>(
    `/playbooks/page?${query}`
  )
}

export const getPlaybooksByGatedPlan = () =>
  ConversationAPI.get<Record<PlaybookReportType, string[]>>(
    '/playbooks/playbooks_by_gate'
  )

export const getPrioritizedPlaybooksByWorkspace = (
  workspaceId: string,
  params: { type: string }
) =>
  ConversationAPI.get<BasicPlaybook[]>(
    `/playbooks/for-priority-in/workspace/${workspaceId}`,
    { params }
  )

export const getPrioritizedPlaybooks = (params: { type: string }) =>
  ConversationAPI.get<BasicPlaybook[]>(`/playbooks/prioritized?`, { params })

export const getPlaybookCounts = () =>
  ConversationAPI.get<Record<PlaybookCategory, number>>(
    '/playbooks/category-counts'
  )

interface FileUploadProps {
  fileUrl: string | null
  fileName: string | null
}

export const uploadPlaybookTranslation = (
  pbId: number | null,
  file: FileUploadProps,
  overwrite: boolean = false,
  name: string
) => {
  return ConversationAPI.post('/playbook_copy/import', {
    overwrite,
    playbookId: pbId,
    file,
    playbookName: name,
  })
}
interface ClonePlaybookParams {
  name: string
  workspace: string
}

interface CloneAsBlockParams {
  name: string
  keepGoalConvoStatus: boolean
}

export const clonePlaybookById = (
  playbookId: string,
  params: ClonePlaybookParams
) =>
  ConversationAPI.post<Playbook>(
    `/playbooks/${playbookId}/clone?${Qs.stringify(params)}`
  )

export const clonePlaybookAsLandingPage = (
  playbookId: number,
  params: ClonePlaybookParams
) =>
  ConversationAPI.post<Playbook>(
    `/playbooks/${playbookId}/clone_as_landing_page?${Qs.stringify(params)}`
  )

export const clonePlaybookAsBot = (
  playbookId: number,
  params: ClonePlaybookParams
) =>
  ConversationAPI.post<Playbook>(
    `/playbooks/${playbookId}/clone_as_bot_playbook?${Qs.stringify(params)}`
  )

export const clonePlaybookAsBlock = (
  playbookId: number,
  params: CloneAsBlockParams
) =>
  ConversationAPI.post<Playbook>(
    `/playbooks/${playbookId}/clone_as_block?${Qs.stringify(params)}`
  )

export const checkCanClonePlaybookAsBlock = (playbookId: number) =>
  ConversationAPI.get(`/playbooks/${playbookId}/can_clone_as_block`)

export const getContentViewExclusions = (playbookId: number) =>
  ConversationAPI.get<Playbook>(
    `/playbooks/${playbookId}/draft/contentViewExclusions`
  )

export const savePriorities = (
  workspaceId: string,
  { priorityList }: { priorityList: number[] },
  params: { type: string }
) =>
  ConversationAPI.patch<Result>(
    `/playbooks/reorder/workspace/${workspaceId}`,
    priorityList,
    { params }
  )

export const saveGlobalPriorities = (
  priorityList: number[],
  params: { type: string }
) =>
  ConversationAPI.patch<Result>(`/playbooks/reorder`, priorityList, { params })

/* ********* */

export const getPlaybook = (playbookId: number) =>
  ConversationAPI.get<Playbook>(`playbooks/${playbookId}`)

export const getPlaybookVersionAt = (playbookId: number, versionAt?: number) =>
  ConversationAPI.get<PlaybookVersionResponse>(
    `playbooks/${playbookId}/version`,
    {
      params: reject(isNil, {
        versionAt,
      }),
    }
  )

export const getPublishedPlaybooksById = (
  playbookId: number,
  beforeTimeStamp?: number
) =>
  ConversationAPI.get<PlaybookVersionResponse[]>(
    `playbooks/${playbookId}/versions`,
    {
      params: reject(isNil, {
        state: 'PUBLISHED',
        beforeTimeStamp,
      }),
    }
  )

export const getClassifications = () =>
  CustomerAPI.get<Classification[]>(`classifications`)

interface SavePlaybookStatusParams {
  id: number
  status: PlaybookStatus
}

export const savePlaybookStatus = ({ status, id }: SavePlaybookStatusParams) =>
  ConversationAPI.patch<Playbook>(`playbooks/${id}/${status}`)

const createUpdateStatus = (status: PlaybookStatus) => (playbookId: number) =>
  savePlaybookStatus({ status, id: playbookId })

export const archivePlaybookById = createUpdateStatus(PlaybookStatus.ARCHIVED)

export const deletePlaybookById = createUpdateStatus(PlaybookStatus.DELETED)

export const disablePlaybookById = createUpdateStatus(PlaybookStatus.DISABLED)

export const enablePlaybookById = createUpdateStatus(PlaybookStatus.ENABLED)

export const savePlaybook = ({ playbook }: { playbook: Playbook }) =>
  ConversationAPI.patch<Playbook>('/playbooks', playbook)

export const checkPlaybook = (playbook: Playbook) =>
  ConversationAPI.post<void>('/playbooks/check', playbook, {
    headers: { 'Content-Type': 'application/json' },
  })

export const saveDraft = ({ playbook }: { playbook: Playbook }) =>
  ConversationAPI.patch<Playbook>(`playbooks/${playbook.id}/draft`, playbook)

export const getDraft = (playbookId: string) =>
  ConversationAPI.get<Playbook>(`playbooks/${playbookId}/draft`)

export const saveLandingPageSettings = (playbookId: number, page: Page) =>
  PagesAPI.put<Page>(`pages/${playbookId}`, page)

export const updateArchiveFlag = (playbookId: number) =>
  PagesAPI.patch<Page>(`pages/${playbookId}`, { isArchived: true })

export const updateRestoreFlag = (playbookId: number) =>
  PagesAPI.patch<Page>(`pages/${playbookId}`, { isArchived: false })

export const updateDeleteFlag = (playbookId: number) =>
  PagesAPI.patch<Page>(`pages/${playbookId}`, { isDeleted: true })

export const getLandingPageSettings = (playbookId: number) =>
  PagesAPI.get<Page>(`pages/${playbookId}`)

export const getLandingPageSubdomain = () =>
  PagesAPI.get<PagesSubdomain>('subdomain')

export const getLandingPageCname = () => PagesAPI.get<PagesCname>('cnames')

export const getLandingPageIntegrations = () =>
  CustomerAPI.get<IntegrationsLandingPagesTrackingSettings>(
    `integrations/landing_pages_tracking`
  )

export const getConversationRatingSettings = (playbookId: number) =>
  ConversationAPI.get<PlaybookConversationRatingSettings>(
    `/conversation_ratings/${playbookId}/settings`
  )

export const saveConversationRatingSettings = (
  id: number,
  {
    isEnabled,
    ratingAck,
    ratingPromptMessage,
  }: PlaybookConversationRatingSettings
) =>
  ConversationAPI.patch<PlaybookConversationRatingSettings>(
    `/conversation_ratings/${id}/settings`,
    {
      isEnabled,
      ratingAck,
      ratingPromptMessage,
    }
  )

export interface ServicePlaybookSettings {
  orgId: number
  playbookId: number
  status: string
  statusValue: number
}

export const getServicePlaybookSettings = (playbookId: number) =>
  ConversationAPI.get<ServicePlaybookSettings>(`/support/playbooks`, {
    params: {
      playbookId: playbookId,
    },
  })

export const saveServicePlaybookSettings = (
  orgId: number,
  playbookId: number,
  isServicePlaybook: boolean
) =>
  ConversationAPI.post(`/support/playbooks`, {
    orgId: orgId,
    playbookId: playbookId,
    status: isServicePlaybook ? 'ENABLED' : 'DISABLED',
  })

export const getClpEmbedSettings = (pageId: number) =>
  PagesAPI.get<PagesEmbedSettings>(`/embed/${pageId}`)

export const saveClpEmbedSettings = (
  pageId: number,
  embedSettings: PagesEmbedSettings
) => PagesAPI.put<PagesEmbedSettings>(`/embed/${pageId}`, embedSettings)

export const getUnfurlSettings = (pageId: number) =>
  PagesAPI.get<PagesUnfurlSettings>(`/unfurls/${pageId}`)

export const updateUnfurlSettings = (
  pageId: number,
  unfurlSettings: PagesUnfurlSettings
) => PagesAPI.put<PagesUnfurlSettings>(`/unfurls/${pageId}`, unfurlSettings)

export const exportPlaybookCopy = (playbookId: string) =>
  ConversationAPI.get<string>(`/playbook_copy/export?playbookId=${playbookId}`)

export const movePlaybookToWorkspace = (
  playbookId: number,
  workspaceId: string
) =>
  ConversationAPI.post<void>(
    `/playbooks/${playbookId}/move-to/workspace/${workspaceId}`
  )

export const getIframelyVideoEmbed = (url: string) =>
  ConversationAPI.post(`/url_validation/playable_video?url=${url}`)

export const getPlaybookShareLink = (playbookId: number) =>
  ReuseApi.post(`/playbook_collaboration_access/link`, {
    id: playbookId,
    sharedContentType: 'PLAYBOOK',
  })

export const fetchCollaboratorAccessLevel = (
  orgId: number,
  playbookId: number
) =>
  ConversationAPI.get<{ accessLevel: PlaybookCollaboratorAccess }>(
    `/playbooks/settings/collaborator_access/${orgId}/${playbookId}`
  )

export const updateCollaboratorAccessLevel = (
  orgId: number,
  playbookId: number,
  accessLevel: PlaybookCollaboratorAccess
) =>
  ConversationAPI.patch<{ accessLevel: PlaybookCollaboratorAccess }>(
    `/playbooks/settings/collaborator_access/${orgId}/${playbookId}`,
    {
      accessLevel,
    }
  )

export const createPlaybook = async ({
  name,
  type,
  workspace,
}: {
  name: string
  type: string
  workspace: string
}) =>
  ConversationAPI.post<Playbook>(
    `playbooks?${QueryString.stringify(
      { name, type, workspace },
      { skipNulls: true }
    )}`
  )

export const getAllContentVariables = (
  nameQuery = null,
  pageSize = 10,
  page = 0,
  direction = 'asc',
  sortProperty = null
) =>
  ReuseApi.get(`/content_variable/all/table`, {
    params: {
      page,
      sortProperty,
      pageSize,
      sortDirection: direction,
      ...(!isEmptyOrNil(nameQuery) ? { nameQuery } : {}),
    },
  })

export const getAllOrgContentVariables = (
  nameQuery = null,
  pageSize = null,
  page = 0,
  direction = 'asc',
  sortProperty = null
) =>
  ReuseApi.get(`/content_variable/all`, {
    params: {
      page,
      sortProperty,
      pageSize,
      sortDirection: direction,
      ...(!isEmptyOrNil(nameQuery) ? { nameQuery } : {}),
    },
  })

export const createNewContentVariable = (name: string, content: string) => {
  return ReuseApi.post(`/content_variable`, {
    name,
    content,
  })
}

interface SingleVariableResponse {
  orgId: number
  name: string
  id: number
  content: string
  archived: boolean
  createdAt: number
  updatedAt: number
  createdBy: number
  updatedBy: number
}

export const updateContentVariable = (variable: SingleVariableResponse) =>
  ReuseApi.patch(`/content_variable`, variable)

export const getContentVariable = (id: number) =>
  ReuseApi.get(`/content_variable/${id}`)

export const deleteContentVariable = (id: number) =>
  ReuseApi.patch(`/content_variable/archive/${id}/true`)

export const getContentVariableUsage = (id: string) =>
  ReuseApi.get(`/content_variable/usage/preview/${id}`)

export const updateContentVariableUsage = (
  variables: SingleVariableResponse[]
) => ReuseApi.post(`/content_variable/usage`, variables)
